import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Code2, Globe, Headphones, Layers, MessageCircle, ShieldCheck, Smartphone, TrendingUp } from 'lucide-react';
import SEO from './seo/SEO';

// ============================================================================
// SERVICE SPECIFIC PRICING & BENEFITS DATA
// ============================================================================
const webSolutionsPlans = [
  {
    name: 'Starter Business Web',
    badge: 'Small Business & Startups',
    price: '₹18,999',
    period: 'One-time investment',
    desc: 'Perfect for local companies, consultants, and service providers who need a fast, professional online presence.',
    popular: false,
    benefits: [
      'Custom 5-page modern responsive website',
      'Mobile-first design (iOS, Android, Tablet)',
      'Lead capture form with instant WhatsApp alerts',
      'Fast loading speed & Core Web Vitals optimized',
      'Free 1st Year SSL Certificate & Domain setup',
      'Google Maps & Local SEO integration',
      '30 Days complimentary post-launch support',
    ],
  },
  {
    name: 'Dynamic Business & CMS',
    badge: 'Growth & Dynamic CMS',
    price: '₹36,999',
    period: 'One-time investment',
    desc: 'Comprehensive web portal with content management system, dynamic catalog, and advanced SEO.',
    popular: true,
    benefits: [
      'Up to 15 pages + dynamic content & blog portal',
      'Admin CMS panel for easy text & image updates',
      'WhatsApp live chat widget & CRM lead capture',
      'Payment gateway integration (Razorpay / Stripe)',
      'Schema markup & On-page SEO optimization',
      'High-speed cloud hosting setup & CDN',
      '3 Months dedicated technical support & maintenance',
    ],
  },
  {
    name: 'Custom Web App & SaaS',
    badge: 'Enterprises & Scale-Ups',
    price: '₹74,999',
    period: 'Starting from / Milestone-based',
    desc: 'Engineered for bespoke web software, client portals, multi-role dashboards, and scalable SaaS solutions.',
    popular: false,
    benefits: [
      'Full-stack custom development (React, Node.js, Next.js)',
      'Role-based access control (Admin, Staff, Customer)',
      'Complex database architecture & REST API sync',
      'Automated invoicing, notifications & reporting',
      'Enterprise-grade security & encrypted data storage',
      'Third-party ERP, CRM, and webhook integrations',
      '6 Months SLA with dedicated technical manager',
    ],
  },
];

const mobileAppsPlans = [
  {
    name: 'Starter Android App',
    badge: 'Single Platform MVP',
    price: '₹39,999',
    period: 'One-time investment',
    desc: 'Rapid MVP designed to validate your mobile idea or streamline field operations on Android devices.',
    popular: false,
    benefits: [
      'Native/Hybrid Android mobile application',
      'Up to 6 core functional screens & clean UX',
      'Secure user phone / OTP / email authentication',
      'Firebase cloud database & real-time sync',
      'Push notification alerts for users',
      'Google Play Store deployment guidance',
      '30 Days post-launch bug fixing & warranty',
    ],
  },
  {
    name: 'Cross-Platform Pro',
    badge: 'iOS & Android Hybrid',
    price: '₹74,999',
    period: 'One-time investment',
    desc: 'Single unified codebase built on Flutter or React Native, delivering silky-smooth performance on both iPhone and Android.',
    popular: true,
    benefits: [
      'Both iOS & Android applications from one build',
      'Up to 15 interactive screens & custom animations',
      'RESTful API integration with your existing backend',
      'In-app online payment gateway (UPI, Cards, Netbanking)',
      'Live GPS location tracking & interactive maps',
      'Full App Store & Play Store publishing management',
      '3 Months dedicated maintenance & updates',
    ],
  },
  {
    name: 'Enterprise Mobile Ecosystem',
    badge: 'Scalable Enterprise Solutions',
    price: '₹1,49,999',
    period: 'Starting from / Custom Scope',
    desc: 'Full-featured enterprise mobility solution with offline data synchronization, hardware pairing, and web admin portal.',
    popular: false,
    benefits: [
      'iOS & Android Apps + Web Admin Management Console',
      'Offline-first architecture with auto cloud sync',
      'Biometric authentication (Face ID / Fingerprint)',
      'Hardware integration (Bluetooth, NFC, RFID, POS printers)',
      'Role-based multi-tier permissions & audit trails',
      'Custom push notification & automated WhatsApp engine',
      '6 Months enterprise SLA with 24/7 technical hotline',
    ],
  },
];

const desktopSoftwarePlans = [
  {
    name: 'Single-Counter Retail POS',
    badge: 'Local Retailers & Shops',
    price: '₹18,999',
    period: 'One-time license',
    desc: 'Fast, offline-first billing and inventory software built for standalone retail counters and billing desks.',
    popular: false,
    benefits: [
      'Ultra-fast desktop billing (keyboard shortcut optimized)',
      'Barcode scanner & thermal receipt printer integration',
      'Item inventory, low stock alerts & purchase records',
      'GST invoice generation (B2B & B2C) with tax reports',
      'Runs 100% offline — never stops during internet cuts',
      'Local automatic daily database backup',
      'Free remote installation & staff training',
    ],
  },
  {
    name: 'Multi-Terminal Business ERP',
    badge: 'Medium Businesses',
    price: '₹44,999',
    period: 'One-time license',
    desc: 'Robust desktop ERP connecting billing counters, stockrooms, accounts, and manager computers via local network (LAN).',
    popular: true,
    benefits: [
      'Multi-counter local LAN sync with centralized data',
      'Advanced inventory: batches, expiry dates & barcodes',
      'Vendor management, purchase orders & credit logs',
      'Customer loyalty points & automated WhatsApp bills',
      'Granular staff permissions & cashier cash drawer balance',
      'Comprehensive P&L, balance sheet & GST filing exports',
      '1 Year priority software support & feature updates',
    ],
  },
  {
    name: 'Multi-Branch Enterprise ERP',
    badge: 'Chains & Large Enterprises',
    price: '₹89,999',
    period: 'Starting from / Custom Scope',
    desc: 'Complete business operating system connecting multiple branches, central warehouse, and remote HQ dashboard.',
    popular: false,
    benefits: [
      'Multi-branch cloud sync with centralized HQ overview',
      'Inter-branch stock transfers & requisition management',
      'Custom manufacturing, BOM, and assembly workflows',
      'Integration with G-Track 360 AI Face / RFID Terminals',
      'Automated daily email reports to business owners',
      'Dedicated migration of existing legacy data/Excel sheets',
      'Dedicated account manager & on-site implementation',
    ],
  },
];

// ============================================================================
// REUSABLE SERVICE PRICING & BENEFITS COMPONENT
// ============================================================================
function ServicePricingSection({ serviceName, tag, subtitle, plans }) {
  const whatsappNumber = '918610452001';

  return (
    <section id="pricing" className="border-t border-slate-200 bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
        
        {/* Banner linking back to Top Software Pricing (G-Track 360 & Billing) */}
        <div className="mb-12 overflow-hidden rounded-2xl border border-blue-200 bg-gradient-to-r from-blue-50 via-indigo-50/50 to-amber-50/50 p-6 sm:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white">
                All-in-One Software Products
              </span>
              <h3 className="mt-2 text-xl font-black text-slate-900 sm:text-2xl">
                Looking for Ready-to-Use Software Pricing?
              </h3>
              <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                Explore transparent annual &amp; lifetime plans for <strong className="text-slate-900">G-Track 360 HRM Software</strong>, <strong className="text-slate-900">G-Track POS Billing Software</strong>, and Biometric / RFID Attendance Terminals.
              </p>
            </div>
            <Link
              to="/pricing"
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white shadow-md transition hover:bg-blue-700 active:scale-95"
            >
              View Full Software Pricing Plans <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-[#bd8741]">
            {tag || 'Transparent Packages'}
          </p>
          <h2 className="mt-2 font-serif text-3xl font-black text-slate-950 sm:text-4xl lg:text-5xl">
            {serviceName} Pricing &amp; Benefits
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-slate-600 sm:text-base">
            {subtitle || 'Clear milestone-based pricing with zero hidden costs, end-to-end deliverables, and dedicated after-launch support.'}
          </p>
        </div>

        {/* Pricing Cards Grid */}
        <div className="mt-12 grid gap-8 pt-4 lg:grid-cols-3 lg:items-stretch">
          {plans.map((plan) => {
            const whatsappMsg = encodeURIComponent(
              `Hello GlobalEPRC, I am interested in the "${plan.name}" plan for ${serviceName} (${plan.price}). Please share the onboarding process and schedule a consultation.`
            );
            const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMsg}`;

            return (
              <div
                key={plan.name}
                className={`relative flex flex-col justify-between rounded-3xl border transition-all duration-300 p-7 sm:p-8 ${
                  plan.popular
                    ? 'border-blue-600 bg-white shadow-xl shadow-blue-500/10 ring-2 ring-blue-600 pt-9 sm:pt-10'
                    : 'border-slate-200 bg-slate-50/70 hover:border-slate-300 hover:bg-white hover:shadow-lg'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10">
                    <span className="whitespace-nowrap rounded-full bg-blue-600 px-4 py-1 text-[11px] font-black uppercase tracking-wider text-white shadow-md">
                      Most Popular
                    </span>
                  </div>
                )}

                <div>
                  {/* Category Badge Row - Identical height on all 3 cards */}
                  <div className="flex items-center min-h-[28px]">
                    <span className={`inline-block rounded-md px-2.5 py-1 text-[11px] font-bold ${
                      plan.popular
                        ? 'bg-blue-50 text-blue-700 border border-blue-200/80'
                        : 'bg-slate-200/80 text-slate-700'
                    }`}>
                      {plan.badge}
                    </span>
                  </div>

                  {/* Title Row - Identical height on all 3 cards */}
                  <h3 className="mt-3 text-xl font-black text-slate-900 min-h-[32px] sm:min-h-[56px] flex items-center">
                    {plan.name}
                  </h3>

                  {/* Description - Identical height on all 3 cards */}
                  <p className="mt-2 text-xs leading-relaxed text-slate-600 min-h-[48px]">
                    {plan.desc}
                  </p>

                  <div className="mt-6 border-y border-slate-200/80 py-4">
                    <div className="flex items-baseline gap-2">
                      <span className="font-serif text-4xl font-black text-slate-950 sm:text-5xl">
                        {plan.price}
                      </span>
                      <span className="text-xs font-semibold text-slate-500">
                        {plan.period}
                      </span>
                    </div>
                  </div>

                  {/* Benefits List */}
                  <div className="mt-6">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-900">
                      Included Benefits &amp; Deliverables:
                    </p>
                    <ul className="mt-3 space-y-3">
                      {plan.benefits.map((benefit, idx) => (
                        <li key={idx} className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-700 leading-snug">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Call to Actions */}
                <div className="mt-8 space-y-2.5 pt-6 border-t border-slate-200/70">
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex w-full items-center justify-center gap-2 rounded-xl py-3 text-xs font-bold transition active:scale-95 ${
                      plan.popular
                        ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md'
                        : 'bg-slate-950 text-white hover:bg-slate-800'
                    }`}
                  >
                    <MessageCircle className="h-4 w-4 text-emerald-300" />
                    Get Started via WhatsApp
                  </a>

                  <Link
                    to="/contact"
                    className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white py-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 transition"
                  >
                    Request Custom Scope &amp; Quote <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom Support & Contact Note */}
        <div className="mt-12 flex flex-col items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center sm:flex-row sm:text-left">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
              <ShieldCheck className="h-5 w-5" />
            </span>
            <div>
              <h4 className="text-sm font-bold text-slate-900">Need a Customized Architecture or Enterprise SLA?</h4>
              <p className="text-xs text-slate-600">Our engineering leads can build custom milestone packages tailored to your scale.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <a
              href="https://wa.me/918610452001?text=Hi%20GlobalEPRC,%20I%20would%20like%20to%20discuss%20a%20custom%20service%20requirement."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-xs font-bold text-white hover:bg-emerald-700 transition"
            >
              <MessageCircle className="h-3.5 w-3.5" /> WhatsApp: +91 8610452001
            </a>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-xs font-bold text-slate-800 hover:bg-slate-100 transition"
            >
              Back to Main Pricing <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
}

const serviceDetails = {
  'web-solutions': {
    title: 'Web Solutions',
    badge: 'Web & SaaS',
    image: '/websolutions-1.png',
    imageAlt: 'Web solutions team building a digital platform',
    icon: Globe,
    intro: 'Fast, secure web experiences that turn your business goals into useful digital products.',
    description: 'From a first company website to a multi-role SaaS platform, we design and build responsive web solutions around the way your customers and teams work.',
    features: ['Custom web portals and SaaS platforms', 'High-converting landing pages', 'Responsive business websites', 'E-commerce platforms and API integrations'],
    technologies: ['React', 'Next.js', 'Tailwind CSS', 'Node.js'],
    outcomes: ['A smooth experience on every screen', 'Secure, maintainable architecture', 'Connected tools and business workflows'],
  },
  'mobile-apps': {
    title: 'Mobile Apps',
    badge: 'iOS & Android',
    image: '/service-2.png',
    imageAlt: 'Mobile app team reviewing a product together',
    icon: Smartphone,
    intro: 'Mobile products that make everyday tasks easier for customers, employees, and field teams.',
    description: 'We create native and cross-platform applications with clear, intuitive interfaces. Apps can support offline work, notifications, and the systems your business already uses.',
    features: ['Native and cross-platform iOS and Android apps', 'Intuitive interface and product design', 'App Store and Google Play launch support', 'Push notifications and offline capabilities'],
    technologies: ['Flutter', 'React Native', 'Swift', 'Kotlin'],
    outcomes: ['Simple, consistent user journeys', 'Reliable use on the move', 'A clear path from prototype to launch'],
  },
  'desktop-software': {
    title: 'Desktop Software',
    badge: 'ERP & Billing',
    image: '/service-3.png',
    imageAlt: 'Desktop software and business operations dashboard',
    icon: Code2,
    intro: 'Purpose-built business software that brings operations, information, and teams together.',
    description: 'We build custom operating systems for your business, including ERP and CRM platforms, point-of-sale tools, and branch workflows tailored to your processes.',
    features: ['Custom ERP and CRM systems', 'Integrated POS and billing', 'Automated data sync across branches', 'Role-based security and permissions'],
    technologies: ['Python', 'Electron', 'PostgreSQL', 'API integrations'],
    outcomes: ['Less repetitive manual work', 'A shared view of business operations', 'Access and reporting tailored to each role'],
  },
  'digital-marketing': {
    title: 'Digital Marketing',
    badge: 'Growth & Leads',
    image: '/service-4.png',
    imageAlt: 'Marketing team planning a digital growth campaign',
    icon: TrendingUp,
    intro: 'Practical digital campaigns that help the right customers discover and choose your business.',
    description: 'We combine paid media, search visibility, and performance measurement to build campaigns around your audience, goals, and budget.',
    features: ['Google and Meta advertising campaigns', 'Local SEO and technical SEO', 'Qualified B2B lead generation', 'Brand growth and campaign reporting'],
    technologies: ['Google Ads', 'Meta Ads', 'GA4', 'Technical SEO'],
    outcomes: ['Clear campaign goals and measurement', 'More relevant traffic and enquiries', 'Ongoing improvements guided by results'],
  },
};

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const service = serviceDetails[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!service) {
    return (
      <section className="mx-auto max-w-4xl px-5 py-32 text-center">
        <h1 className="text-3xl font-black text-slate-950">Service not found</h1>
        <Link to="/#services" className="mt-6 inline-flex items-center gap-2 font-bold text-blue-700">Back to services <ArrowRight className="h-4 w-4" /></Link>
      </section>
    );
  }

  if (slug === 'web-solutions') return <WebSolutionsLanding />;
  if (slug === 'mobile-apps') return <MobileAppsLanding />;
  if (slug === 'desktop-software') return <DesktopSoftwareLanding />;

  const Icon = service.icon;

  return (
    <div className="bg-white text-slate-900">
      <SEO title={`${service.title} | GlobalEPRC`} description={service.intro} path={`/services/${slug}`} />
      <section className="relative isolate min-h-[480px] overflow-hidden bg-slate-950 pt-28 text-white sm:min-h-[540px] sm:pt-32">
        <img src={service.image} alt={service.imageAlt} className="absolute inset-0 -z-20 h-full w-full object-cover" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-slate-950/95 via-slate-950/75 to-slate-950/25" />
        <div className="mx-auto flex min-h-[380px] max-w-7xl items-center px-5 py-16 sm:min-h-[410px] sm:px-8 lg:px-10">
          <div className="max-w-3xl">
            <Link to="/#services" className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-white/80 transition hover:text-white"><ArrowLeft className="h-4 w-4" /> All services</Link>
            <div className="flex items-center gap-3 text-blue-200">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/20 bg-white/10"><Icon className="h-5 w-5" /></span>
              <span className="text-xs font-black uppercase tracking-[0.2em]">{service.badge}</span>
            </div>
            <h1 className="mt-5 text-4xl font-black tracking-tight sm:text-6xl">{service.title}</h1>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-white/85 sm:text-xl">{service.intro}</p>
            <Link to="/contact" className="mt-8 inline-flex items-center gap-3 rounded-xl bg-amber-400 px-6 py-3.5 text-sm font-black text-slate-950 transition hover:bg-amber-300">Talk to our team <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-12 px-5 py-16 sm:px-8 sm:py-20 lg:grid-cols-[1.1fr_0.9fr] lg:px-10">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.2em] text-blue-700">What we do</p>
          <h2 className="mt-3 text-3xl font-black leading-tight text-slate-950 sm:text-4xl">Technology shaped around your business.</h2>
          <p className="mt-5 max-w-2xl leading-relaxed text-slate-600">{service.description}</p>

          <h3 className="mt-10 text-xl font-extrabold text-slate-950">Our capabilities</h3>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {service.features.map((feature) => (
              <div key={feature} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm leading-relaxed text-slate-700">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        <aside className="rounded-2xl bg-slate-950 p-6 text-white sm:p-8">
          <h3 className="text-xl font-extrabold">Built for real-world results</h3>
          <ul className="mt-6 space-y-4">
            {service.outcomes.map((outcome) => (
              <li key={outcome} className="flex items-start gap-3 text-sm leading-relaxed text-slate-300">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                <span>{outcome}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 border-t border-white/15 pt-6">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-300">Technologies & channels</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {service.technologies.map((technology) => <span key={technology} className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/85">{technology}</span>)}
            </div>
          </div>
          <Link to="/contact" className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-amber-300 transition hover:text-amber-200">Discuss your project <ArrowRight className="h-4 w-4" /></Link>
        </aside>
      </section>

      <ServicePricingSection
        serviceName={service.title}
        tag="Investment & Plans"
        subtitle={`Select a milestone-driven plan for ${service.title} with clear deliverables and dedicated technical support.`}
        plans={slug === 'mobile-apps' ? mobileAppsPlans : slug === 'desktop-software' ? desktopSoftwarePlans : webSolutionsPlans}
      />

      <section className="bg-blue-50 px-5 py-12 sm:px-8">
        <div className="mx-auto flex max-w-7xl flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-2xl font-black text-slate-950">Have a project in mind?</h2>
            <p className="mt-2 text-sm text-slate-600">Tell us what you’re building and we’ll help plan the next step.</p>
          </div>
          <Link to="/contact" className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-950 px-5 py-3 text-sm font-bold text-white transition hover:bg-blue-700">Contact GlobalEPRC <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>
    </div>
  );
};

const WebSolutionsLanding = () => {
  return (
    <div id="top" className="bg-[#f8f6f1] font-sans text-slate-900">
      <SEO title="Web Solutions | GlobalEPRC" description="Custom web solutions tailored to your business, from responsive websites and e-commerce platforms to web applications and CMS development." path="/services/web-solutions" />

      <main>
        {/* HERO SECTION */}
        <section className="relative isolate flex min-h-[480px] items-center overflow-hidden bg-slate-950 pt-16 text-white sm:min-h-[520px] sm:pt-20">
          <img src="/websolutions-1.png" alt="Web design team planning a digital experience" className="absolute inset-0 -z-20 h-full w-full object-cover object-center" />
          <div className="absolute inset-0 -z-10 bg-gradient-to-r from-black/85 via-black/55 to-black/10" />
          <div className="mx-auto w-full max-w-7xl px-5 py-14 sm:px-8 sm:py-16 lg:px-10">
            <div className="max-w-[560px]">
              <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#e6b96f]">Web Solutions</p>
              <h1 className="mt-3 font-serif text-4xl leading-[1.05] sm:text-5xl">Web Experiences<br />Built for <span className="text-[#e6b96f]">Real Growth</span></h1>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-white/90">From simple business websites to complex web applications, we create digital experiences that help you attract, engage and grow.</p>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <a href="#pricing" className="inline-flex items-center gap-2 rounded-md bg-[#e5b76b] px-5 py-3 text-[11px] font-bold text-slate-950 transition hover:bg-[#f1ca87]">View Packages &amp; Pricing <ArrowRight className="h-3.5 w-3.5" /></a>
                <Link to="/contact" className="inline-flex items-center gap-2 rounded-md border border-white/20 bg-white/10 px-5 py-3 text-[11px] font-bold text-white transition hover:bg-white/20">Discuss Your Project</Link>
              </div>
            </div>
          </div>
        </section>

        {/* PACKAGES & PRICING */}
        <ServicePricingSection
          serviceName="Web Solutions & Development"
          tag="Web Packages & Pricing"
          subtitle="Fixed transparent pricing for high-performing websites, e-commerce stores, and custom web applications."
          plans={webSolutionsPlans}
        />
      </main>
    </div>
  );
};

const MobileAppsLanding = () => {
  return (
    <div className="bg-[#faf8f4] font-sans text-slate-900">
      <SEO title="Mobile App Development | GlobalEPRC" description="User-friendly Android and iOS applications built around your business goals." path="/services/mobile-apps" />
      <main>
        {/* HERO SECTION */}
        <section className="relative isolate flex min-h-[480px] items-center overflow-hidden bg-[#f7f3eb] pt-20 sm:min-h-[520px] sm:pt-24">
          <img src="/mobileapp-1.png" alt="Food ordering mobile app displayed on a phone" className="absolute inset-0 -z-20 h-full w-full object-cover object-center" />
          <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#faf8f4]/45 via-[#faf8f4]/20 to-transparent" />
          <div className="mx-auto w-full max-w-7xl px-5 py-12 sm:px-8 lg:px-10">
            <div className="max-w-[520px]">
              <p className="inline-flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.2em] text-slate-800">Mobile Apps <span className="h-px w-12 bg-[#c99148]" /></p>
              <h1 className="mt-6 font-serif text-4xl leading-[1.08] text-slate-950 sm:text-5xl">Mobile Applications<br />for Growing Businesses</h1>
              <p className="mt-5 max-w-md text-sm leading-relaxed text-slate-700 sm:text-base">We design and develop Android and iOS applications that are user-friendly, scalable and built around your business goals.</p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <a href="#pricing" className="inline-flex items-center gap-2 rounded-md bg-[#d9aa62] px-5 py-3 text-xs font-bold text-slate-950 transition hover:bg-[#e9bd79]">View Packages &amp; Pricing <ArrowRight className="h-3.5 w-3.5" /></a>
                <Link to="/contact" className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white/80 px-5 py-3 text-xs font-bold text-slate-900 transition hover:bg-white">Discuss Your Project</Link>
              </div>
            </div>
          </div>
        </section>

        {/* PACKAGES & PRICING */}
        <ServicePricingSection
          serviceName="Mobile App Development"
          tag="Mobile App Packages & Pricing"
          subtitle="Fixed milestone packages for intuitive, scalable iOS and Android applications with zero hidden fees."
          plans={mobileAppsPlans}
        />
      </main>
    </div>
  );
};

const DesktopSoftwareLanding = () => {
  return (
    <div className="bg-[#fbf8f2] font-sans text-slate-900">
      <SEO title="Desktop Software | GlobalEPRC" description="Custom desktop software for inventory, billing, HR, finance and business operations." path="/services/desktop-software" />
      <main>
        {/* HERO SECTION */}
        <section className="relative isolate flex min-h-[480px] items-center overflow-hidden bg-[#f7f2e9] pt-20 sm:min-h-[520px] sm:pt-24">
          <img src="/desktop-software-1.png" alt="Business desktop software dashboard on a computer monitor" className="absolute inset-0 -z-20 h-full w-full object-cover object-[58%_center]" />
          <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#fbf8f2]/95 via-[#fbf8f2]/75 to-transparent" />
          <div className="mx-auto w-full max-w-7xl px-5 py-16 sm:px-8 lg:px-10">
            <div className="max-w-[470px]">
              <p className="inline-flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.2em] text-slate-800">Desktop Software <span className="h-px w-12 bg-[#c99148]" /></p>
              <h1 className="mt-6 font-serif text-4xl leading-[1.08] text-slate-950 sm:text-5xl">Reliable Desktop<br />Software Solutions</h1>
              <p className="mt-5 max-w-md text-sm leading-relaxed text-slate-700 sm:text-base">We design and develop custom desktop applications that simplify your business operations, improve productivity and deliver secure, high-performance solutions tailored to your needs.</p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <a href="#pricing" className="inline-flex items-center gap-2 rounded-md bg-[#d9aa62] px-5 py-3 text-xs font-bold text-slate-950 transition hover:bg-[#e9bd79]">View Packages &amp; Pricing <ArrowRight className="h-3.5 w-3.5" /></a>
                <Link to="/contact" className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white/80 px-5 py-3 text-xs font-bold text-slate-900 transition hover:bg-white">Discuss Your Project</Link>
              </div>
            </div>
          </div>
        </section>

        {/* PACKAGES & PRICING */}
        <ServicePricingSection
          serviceName="Desktop Software & ERP"
          tag="Desktop & ERP Licensing"
          subtitle="One-time perpetual licensing with zero recurring AMC options, full offline support, and multi-terminal sync."
          plans={desktopSoftwarePlans}
        />
      </main>
    </div>
  );
};

export default ServiceDetailPage;
